import React, { useState, useEffect } from 'react';
import { Instagram } from 'lucide-react';
import './App.css';

import menuData from './menuData.json';
import {
  MenuCard,
  CategoryFilters,
  RecommendButton,
  AdminButtons,
  AddMenuForm,
  EditMenuForm,
  MenuList,
  Notification,
  Footer
} from './MenuComponents';

function App() {
  // 이모지 선택 목록
  const emojiOptions = ['🍲', '🍚', '🍜', '🥘', '🍱', '🥓', '🥩', '🍗', '🍖', '🍣'];
  
  // 상태 관리
  const [lunchMenus, setLunchMenus] = useState(() => {
    // 로컬 스토리지에서 메뉴 불러오기 시도
    const savedMenus = localStorage.getItem('lunchMenus');
    return savedMenus ? JSON.parse(savedMenus) : menuData;
  });
  
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [animating, setAnimating] = useState(false);
  const [filter, setFilter] = useState('전체');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditMode, setShowEditMode] = useState(false);
  const [notification, setNotification] = useState('');
  const [editingMenu, setEditingMenu] = useState(null);
  
  // 새 메뉴 상태
  const [newMenu, setNewMenu] = useState({
    name: '',
    description: '',
    calories: '',
    price: '',
    category: '',
    emoji: '🍲'
  });
  
  // 커스텀 카테고리 관리
  const [newCategory, setNewCategory] = useState('');
  const [categories, setCategories] = useState(() => {
    const uniqueCategories = [...new Set(lunchMenus.map(menu => menu.category))];
    return uniqueCategories;
  });
  
  // 메뉴 변경시 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem('lunchMenus', JSON.stringify(lunchMenus));
    const uniqueCategories = [...new Set(lunchMenus.map(menu => menu.category))];
    setCategories(uniqueCategories);
  }, [lunchMenus]);
  
  // 알림 표시 및 자동 제거
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification('');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [notification]);
  
  // 랜덤 메뉴 선택 함수
  const getRandomMenu = () => {
    setAnimating(true);
    
    // 필터링된 메뉴 목록 생성
    const filteredMenus = filter === '전체' 
      ? lunchMenus 
      : lunchMenus.filter(menu => menu.category === filter);
    
    if (filteredMenus.length === 0) {
      setAnimating(false);
      showNotification('해당 카테고리에 메뉴가 없습니다.');
      return; // 메뉴가 없으면 함수 종료
    }
    
    // 랜덤 인덱스 선택을 위한 타이머 효과 - 정확히 5번만 점핑
    let count = 0;
    const maxCount = 5;
    const interval = setInterval(() => {
      const randomIndex = Math.floor(Math.random() * filteredMenus.length);
      setSelectedMenu(filteredMenus[randomIndex]);
      count++;
      
      if (count >= maxCount) {
        clearInterval(interval);
        setAnimating(false);
      }
    }, 300);
  };
  
  // 새 메뉴 입력 핸들러
  const handleNewMenuChange = (e) => {
    const { name, value } = e.target;
    setNewMenu({
      ...newMenu,
      [name]: value
    });
  };
  
  // 이모지 선택 핸들러
  const handleEmojiSelect = (emoji) => {
    setNewMenu({
      ...newMenu,
      emoji: emoji
    });
  };
  
  // 새 카테고리 추가 핸들러
  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory)) {
      setCategories([...categories, newCategory]);
      
      // 현재 수정 중인 메뉴가 있으면 해당 메뉴의 카테고리 변경
      if (editingMenu) {
        setEditingMenu({
          ...editingMenu,
          category: newCategory
        });
      } else {
        // 새 메뉴에 카테고리 적용
        setNewMenu({
          ...newMenu,
          category: newCategory
        });
      }
      
      setNewCategory('');
      showNotification(`새 카테고리 "${newCategory}"가 추가되었습니다.`);
    } else if (categories.includes(newCategory)) {
      showNotification('이미 존재하는 카테고리입니다.');
    }
  };
  
  // 알림 표시 함수
  const showNotification = (message) => {
    setNotification(message);
  };
  
  // 새 메뉴 추가 핸들러
  const handleAddMenu = () => {
    try {
      // 필수 필드 검증
      if (!newMenu.name || !newMenu.category) {
        showNotification('메뉴 이름과 카테고리는 필수 입력 항목입니다.');
        return;
      }
      
      // 새 ID 생성
      const maxId = lunchMenus.length > 0 ? 
        Math.max(...lunchMenus.map(menu => menu.id)) : 0;
      
      const menuToAdd = {
        ...newMenu,
        id: maxId + 1
      };
      
      // 메뉴 추가
      const updatedMenus = [...lunchMenus, menuToAdd];
      setLunchMenus(updatedMenus);
      
      // 입력 폼 초기화
      setNewMenu({
        name: '',
        description: '',
        calories: '',
        price: '',
        category: '',
        emoji: '🍲'
      });
      
      setShowAddForm(false);
      
      // 추가 성공 알림
      showNotification(`"${menuToAdd.name}" 메뉴가 추가되었습니다.`);
    } catch (error) {
      console.error('메뉴 추가 중 오류 발생:', error);
      showNotification('메뉴 추가 중 오류가 발생했습니다.');
    }
  };
  
  // 메뉴 삭제 핸들러
  const handleDeleteMenu = (id) => {
    const menuToDelete = lunchMenus.find(menu => menu.id === id);
    
    if (!menuToDelete) {
      console.error('삭제할 메뉴를 찾을 수 없습니다:', id);
      return;
    }
    
    try {
      const confirmDelete = window.confirm(`"${menuToDelete.name}" 메뉴를 삭제하시겠습니까?`);
      
      if (confirmDelete) {
        // 메뉴 배열에서 해당 ID를 가진 메뉴를 제외한 새 배열 생성
        const updatedMenus = lunchMenus.filter(menu => menu.id !== id);
        
        // 상태 업데이트
        setLunchMenus(updatedMenus);
        
        // 선택된 메뉴가 삭제되는 경우 선택 취소
        if (selectedMenu && selectedMenu.id === id) {
          setSelectedMenu(null);
        }
        
        // 수정 중인 메뉴가 삭제되는 경우 수정 취소
        if (editingMenu && editingMenu.id === id) {
          setEditingMenu(null);
        }
        
        // 삭제 성공 알림
        showNotification(`"${menuToDelete.name}" 메뉴가 삭제되었습니다.`);
      }
    } catch (error) {
      console.error('메뉴 삭제 중 오류 발생:', error);
      showNotification('메뉴 삭제 중 오류가 발생했습니다.');
    }
  };
  
  // 메뉴 수정 핸들러 - 수정 모드 시작
  const handleEditStart = (id) => {
    const menuToEdit = lunchMenus.find(menu => menu.id === id);
    if (menuToEdit) {
      setEditingMenu({...menuToEdit});
      setShowAddForm(false);
    }
  };
  
  // 메뉴 수정 입력 핸들러
  const handleEditMenuChange = (e) => {
    const { name, value } = e.target;
    setEditingMenu({
      ...editingMenu,
      [name]: value
    });
  };
  
  // 메뉴 수정 이모지 선택 핸들러
  const handleEditEmojiSelect = (emoji) => {
    setEditingMenu({
      ...editingMenu,
      emoji: emoji
    });
  };
  
  // 메뉴 수정 저장 핸들러
  const handleEditSave = () => {
    try {
      // 필수 필드 검증
      if (!editingMenu.name || !editingMenu.category) {
        showNotification('메뉴 이름과 카테고리는 필수 입력 항목입니다.');
        return;
      }
      
      // 기존 메뉴 목록에서 수정중인 메뉴 업데이트
      const updatedMenus = lunchMenus.map(menu => 
        menu.id === editingMenu.id ? editingMenu : menu
      );
      
      setLunchMenus(updatedMenus);
      
      // 선택된 메뉴가 수정되는 경우 업데이트
      if (selectedMenu && selectedMenu.id === editingMenu.id) {
        setSelectedMenu(editingMenu);
      }
      
      // 수정 모드 종료
      setEditingMenu(null);
      
      // 성공 알림
      showNotification(`"${editingMenu.name}" 메뉴가 수정되었습니다.`);
    } catch (error) {
      console.error('메뉴 수정 중 오류 발생:', error);
      showNotification('메뉴 수정 중 오류가 발생했습니다.');
    }
  };
  
  // 메뉴 수정 취소 핸들러
  const handleEditCancel = () => {
    setEditingMenu(null);
  };
  
  // 초기 메뉴로 리셋하는 핸들러
  const handleResetMenus = () => {
    try {
      const confirmReset = window.confirm('모든 메뉴를 초기 상태로 되돌리시겠습니까? 추가된 메뉴는 모두 삭제됩니다.');
      
      if (confirmReset) {
        // 깊은 복사를 사용하여 초기 메뉴 배열 새로 생성
        const resetMenus = JSON.parse(JSON.stringify(menuData));
        
        // 상태 업데이트
        setLunchMenus(resetMenus);
        setSelectedMenu(null);
        setFilter('전체');
        setEditingMenu(null);
        
        // 리셋 성공 알림
        showNotification('초기 메뉴로 리셋되었습니다.');
      }
    } catch (error) {
      console.error('메뉴 리셋 중 오류 발생:', error);
      showNotification('메뉴 리셋 중 오류가 발생했습니다.');
    }
  };
  
  // 소셜 미디어 공유 핸들러
  const handleShare = (platform) => {
    if (!selectedMenu) {
      showNotification('공유할 메뉴를 먼저 선택해주세요.');
      return;
    }
    
    const shareText = `오늘의 점심 메뉴로 ${selectedMenu.name}(${selectedMenu.category})은 어떨까요? ${selectedMenu.description} [칼로리: ${selectedMenu.calories}, 가격: ${selectedMenu.price}]`;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(window.location.href)}`, '_blank');
        break;
      case 'kakao':
        // 카카오 SDK가 필요하므로 알림으로 대체
        showNotification('카카오 공유를 위해서는 Kakao SDK가 필요합니다.');
        break;
      default:
        // 클립보드에 복사
        navigator.clipboard.writeText(shareText)
          .then(() => showNotification('클립보드에 복사되었습니다.'))
          .catch(err => {
            console.error('클립보드 복사 실패:', err);
            showNotification('클립보드 복사에 실패했습니다.');
          });
    }
  };

  return (
    <div className="App">
      <div className="lunch-recommender">
        <h1 className="app-title">오늘의 점심 메뉴 추천</h1>
        
        {/* 카테고리 필터 */}
        <CategoryFilters 
          categories={categories} 
          filter={filter} 
          setFilter={setFilter}
        />
        
        {/* 메뉴 추천 버튼 */}
        <RecommendButton 
          getRandomMenu={getRandomMenu} 
          animating={animating} 
          disabled={lunchMenus.length === 0}
        />
        
        {/* 추천 메뉴 카드 */}
        {selectedMenu && (
          <MenuCard 
            menu={selectedMenu} 
            animating={animating} 
            handleShare={handleShare}
          />
        )}
        
        {/* 메뉴 관리 버튼 */}
        <AdminButtons 
          showAddForm={showAddForm} 
          setShowAddForm={setShowAddForm} 
          showEditMode={showEditMode} 
          setShowEditMode={setShowEditMode} 
          editingMenu={editingMenu} 
          setEditingMenu={setEditingMenu} 
          handleResetMenus={handleResetMenus}
        />
        
        {/* 새 메뉴 추가 폼 */}
        {showAddForm && (
          <AddMenuForm 
            newMenu={newMenu}
            handleNewMenuChange={handleNewMenuChange}
            categories={categories}
            newCategory={newCategory}
            setNewCategory={setNewCategory}
            handleAddCategory={handleAddCategory}
            emojiOptions={emojiOptions}
            handleEmojiSelect={handleEmojiSelect}
            setShowAddForm={setShowAddForm}
            handleAddMenu={handleAddMenu}
          />
        )}
        
        {/* 메뉴 수정 폼 */}
        {editingMenu && (
          <EditMenuForm 
            editingMenu={editingMenu}
            handleEditMenuChange={handleEditMenuChange}
            categories={categories}
            newCategory={newCategory}
            setNewCategory={setNewCategory}
            handleAddCategory={handleAddCategory}
            emojiOptions={emojiOptions}
            handleEditEmojiSelect={handleEditEmojiSelect}
            handleEditCancel={handleEditCancel}
            handleEditSave={handleEditSave}
          />
        )}
        
        {/* 메뉴 목록 */}
        <MenuList 
          lunchMenus={lunchMenus}
          filter={filter}
          showEditMode={showEditMode}
          setSelectedMenu={setSelectedMenu}
          handleEditStart={handleEditStart}
          handleDeleteMenu={handleDeleteMenu}
        />
      </div>
      
      {/* 알림 메시지 */}
      <Notification message={notification} />
      
      {/* 푸터 */}
      <Footer />
    </div>
  );
}

export default App;